export const ANIM_DEFAULTS = {
  stacked_swipe: { enabled: false, speed: 1, intensity: 5, desktop: true, mobile: true, target: "new-arrivals" },
  hover_reveal: { enabled: true, speed: 1, intensity: 5, desktop: true, mobile: false, target: "all" },
  scroll_reveal: { enabled: true, speed: 1, intensity: 4, desktop: true, mobile: true, target: "all" },
  tilt_3d: { enabled: false, speed: 1, intensity: 3, desktop: true, mobile: false, target: "new-arrivals" },
  magnetic_buttons: { enabled: false, speed: 1, intensity: 4, desktop: true, mobile: false, target: "all_ctas" },
  text_reveal: { enabled: true, speed: 1, intensity: 4, desktop: true, mobile: true, target: "all" },
  hero_parallax: { enabled: true, speed: 1, intensity: 3, desktop: true, mobile: false, target: "hero" },
};

export function mergeAnim(saved) {
  const out = {};
  for (const k of Object.keys(ANIM_DEFAULTS)) out[k] = { ...ANIM_DEFAULTS[k], ...((saved || {})[k] || {}) };
  return out;
}

export function getAnimConfig(settings) {
  try {
    if (new URLSearchParams(window.location.search).has("anim_preview")) {
      const draft = JSON.parse(localStorage.getItem("tq_anim_draft") || "null");
      if (draft) return mergeAnim(draft);
    }
  } catch { /* ignore */ }
  return mergeAnim(settings?.animations);
}

const isPreview = () => {
  try { return new URLSearchParams(window.location.search).has("anim_preview"); } catch { return false; }
};

function showPreviewBadge() {
  const el = document.createElement("div");
  el.setAttribute("data-testid", "anim-preview-badge");
  const bottom = window.matchMedia("(max-width: 768px)").matches ? "76px" : "14px";
  el.style.cssText = `position:fixed;bottom:${bottom};left:14px;z-index:9999;background:#111111;color:#C59B27;font:600 11px/1 'Plus Jakarta Sans',sans-serif;letter-spacing:.18em;text-transform:uppercase;padding:10px 14px;border:1px solid #C59B27;pointer-events:none`;
  el.textContent = "Animation Preview — not published";
  document.body.appendChild(el);
  return () => el.remove();
}

function restore(el, props) {
  props.forEach((p) => { el.style[p] = ""; });
}

function scopeSel(target, fallback = "[data-section]") {
  return !target || target === "all" ? fallback : `[data-section="${target}"]`;
}

/* 1. Stacked swipe deck for product cards — visible stack, prev/next arrows, bidirectional */
function stackedSwipe(c) {
  const sec = document.querySelector(`[data-section="${c.target}"]`);
  const grid = sec?.querySelector("[data-anim-grid]");
  if (!grid) return null;
  const cards = [...grid.children];
  if (cards.length < 2) return null;
  const n = cards.length;
  const maxVisible = Math.min(3, n - 1);
  const offX = Math.round(8 + c.intensity * 2.6);
  const offY = Math.round(5 + c.intensity * 1.4);
  const scaleStep = 0.028 + c.intensity * 0.004;
  const dur = 0.45 / c.speed;
  const ease = "cubic-bezier(.22,.8,.26,1)";
  const compact = window.matchMedia("(max-width: 560px)").matches;
  const gridW = grid.clientWidth || 420;
  const saved = [grid, ...cards].map((el) => el.style.cssText);

  grid.style.position = "relative";
  grid.style.maxWidth = "min(420px, 100%)";
  grid.style.margin = "0 auto 44px";
  grid.style.overflow = "visible";
  grid.style.touchAction = "pan-y";

  const state = cards.map((el) => ({ el }));
  const place = (s, pos, animate = true) => {
    const d = Math.min(pos, maxVisible);
    s.el.style.position = "absolute";
    s.el.style.top = "0"; s.el.style.left = "0"; s.el.style.width = "100%";
    s.el.style.zIndex = String(n - pos);
    s.el.style.transition = animate ? `transform ${dur}s ${ease}, opacity ${dur}s ease, box-shadow ${dur}s ease` : "none";
    s.el.style.transform = `translate(${d * offX}px, ${d * offY}px) scale(${(1 - pos * scaleStep).toFixed(3)})`;
    s.el.style.opacity = pos > maxVisible ? "0" : "1";
    s.el.style.pointerEvents = pos === 0 ? "auto" : "none";
    s.el.style.background = "#FFFFFF";
    s.el.style.border = "1px solid #EAEAEA";
    s.el.style.boxShadow = pos <= maxVisible ? `0 ${12 + pos * 5}px ${30 + pos * 8}px rgba(17,17,17,${Math.max(0.05, 0.17 - pos * 0.04).toFixed(3)})` : "none";
    s.el.style.touchAction = "pan-y";
  };
  const layout = (animate = true) => state.forEach((s, pos) => place(s, pos, animate));

  const setHeight = () => {
    const h = Math.max(...cards.map((k) => k.offsetHeight)) + offY * maxVisible + 16;
    if (h > 10) grid.style.height = `${h}px`;
  };
  setHeight();
  layout(false);
  const t2 = setTimeout(setHeight, 800);

  cards.forEach((el) => {
    el.style.userSelect = "none";
    el.querySelectorAll("a, img").forEach((nd) => { nd.setAttribute("draggable", "false"); nd.style.userSelect = "none"; });
  });

  // Position counter
  let idx = 0;
  const counterEl = document.createElement("div");
  counterEl.setAttribute("data-testid", "stack-counter");
  counterEl.style.cssText = "position:absolute;left:50%;transform:translateX(-50%);bottom:-34px;font:600 11px/1 'Plus Jakarta Sans',sans-serif;letter-spacing:.22em;color:#8E8E93;text-transform:uppercase;pointer-events:none;white-space:nowrap";
  grid.appendChild(counterEl);
  const counter = () => { counterEl.textContent = `${idx + 1} / ${n}`; };
  counter();

  let busy = false;
  const flyOut = (el, dir, cb) => {
    el.style.transition = `transform ${dur}s ease, opacity ${dur}s ease`;
    el.style.transform = `translate(${dir * gridW * 1.2}px, 48px) rotate(${dir * 9}deg)`;
    el.style.opacity = "0";
    setTimeout(cb, dur * 1000);
  };
  const next = () => {
    if (busy) return; busy = true;
    const moved = state[0];
    idx = (idx + 1) % n; counter();
    flyOut(moved.el, -1, () => {
      state.push(state.shift());
      place(moved, state.length - 1, false);
      layout(true);
      setTimeout(() => { busy = false; }, 60);
    });
  };
  const prev = () => {
    if (busy) return; busy = true;
    const moved = state[state.length - 1];
    idx = (idx - 1 + n) % n; counter();
    moved.el.style.transition = "none";
    moved.el.style.transform = `translate(${gridW * 1.2}px, 48px) rotate(9deg)`;
    moved.el.style.opacity = "0";
    moved.el.style.zIndex = String(n + 1);
    state.unshift(state.pop());
    requestAnimationFrame(() => requestAnimationFrame(() => {
      layout(true);
      setTimeout(() => { busy = false; }, dur * 1000 + 60);
    }));
  };

  // Prev / Next arrows
  const mkBtn = (dir) => {
    const b = document.createElement("button");
    b.type = "button";
    b.setAttribute("aria-label", dir === -1 ? "Previous product" : "Next product");
    b.setAttribute("data-testid", dir === -1 ? "stack-prev-btn" : "stack-next-btn");
    b.innerHTML = dir === -1
      ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>'
      : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>';
    const size = compact ? 38 : 46;
    const side = compact ? "8px" : "-62px";
    b.style.cssText = `position:absolute;top:38%;${dir === -1 ? `left:${side}` : `right:${side}`};z-index:${n + 2};width:${size}px;height:${size}px;border-radius:50%;background:${compact ? "rgba(255,255,255,.94)" : "#FFFFFF"};border:1px solid #EAEAEA;color:#111111;display:flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 8px 22px rgba(17,17,17,.12);transition:all .25s ease;padding:0`;
    b.onmouseenter = () => { b.style.borderColor = "#C59B27"; b.style.color = "#A8821B"; b.style.transform = "scale(1.08)"; b.style.boxShadow = "0 12px 28px rgba(17,17,17,.18)"; };
    b.onmouseleave = () => { b.style.borderColor = "#EAEAEA"; b.style.color = "#111111"; b.style.transform = "scale(1)"; b.style.boxShadow = "0 8px 22px rgba(17,17,17,.12)"; };
    b.addEventListener("click", (e) => { e.stopPropagation(); e.preventDefault(); dir === -1 ? prev() : next(); });
    return b;
  };
  const prevBtn = mkBtn(-1);
  const nextBtn = mkBtn(1);
  grid.appendChild(prevBtn);
  grid.appendChild(nextBtn);

  // Drag / swipe
  let startX = null, dx = 0, dragged = false;
  const top = () => state[0]?.el;
  const onDown = (e) => {
    if (!top() || busy) return;
    startX = e.clientX ?? e.touches?.[0]?.clientX;
    dx = 0; dragged = false;
  };
  const onMove = (e) => {
    if (startX === null || !top()) return;
    dx = (e.clientX ?? e.touches?.[0]?.clientX) - startX;
    if (Math.abs(dx) > 8) dragged = true;
    top().style.transition = "none";
    top().style.transform = `translate(${dx}px, ${Math.abs(dx) * 0.08}px) rotate(${dx * 0.04}deg)`;
  };
  const onUp = () => {
    if (startX === null) return;
    startX = null;
    if (dx < -70) next();
    else if (dx > 70) prev();
    else layout(true);
  };
  const swallowClick = (e) => { if (dragged) { e.preventDefault(); e.stopPropagation(); dragged = false; } };
  const onCancel = () => { if (startX !== null) { startX = null; layout(true); } };
  const onDragStart = (e) => e.preventDefault();
  grid.addEventListener("dragstart", onDragStart);
  grid.addEventListener("pointerdown", onDown);
  window.addEventListener("pointermove", onMove, { passive: true });
  window.addEventListener("pointerup", onUp);
  grid.addEventListener("pointercancel", onCancel);
  grid.addEventListener("click", swallowClick, true);
  return () => {
    clearTimeout(t2);
    prevBtn.remove(); nextBtn.remove(); counterEl.remove();
    grid.removeEventListener("dragstart", onDragStart);
    grid.removeEventListener("pointerdown", onDown);
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    grid.removeEventListener("pointercancel", onCancel);
    grid.removeEventListener("click", swallowClick, true);
    [grid, ...cards].forEach((el, i) => { el.style.cssText = saved[i]; });
  };
}

/* 2. Hover / second image reveal (controls the built-in card hover swap) */
function hoverReveal(c) {
  const sel = `${scopeSel(c.target)} [data-testid^="product-card-"]`;
  const cards = document.querySelectorAll(sel);
  const touched = [];
  cards.forEach((card) => {
    const imgs = card.querySelectorAll("a img");
    if (imgs.length < 2) return;
    const [a, b] = imgs;
    touched.push([a, a.style.cssText], [b, b.style.cssText]);
    if (!c.enabled) { b.style.display = "none"; return; }
    const dur = `${(0.5 / c.speed).toFixed(2)}s`;
    a.style.transitionDuration = dur;
    b.style.transitionDuration = dur;
  });
  return () => touched.forEach(([el, css]) => { el.style.cssText = css; });
}

/* 3. Scroll reveal */
function scrollReveal(c, cfg) {
  const stackedT = cfg?.stacked_swipe?.enabled ? cfg.stacked_swipe.target : null;
  let els = [...document.querySelectorAll(`${scopeSel(c.target)} [data-testid^="product-card-"], ${scopeSel(c.target)} .tq-card`)];
  // The stacked deck owns its cards' transforms — never overwrite them
  if (stackedT) els = els.filter((el) => !el.closest(`[data-section="${stackedT}"]`));
  if (!els.length) return null;
  const dur = 0.7 / c.speed;
  const rise = Math.round(14 * (c.intensity / 5));
  const saved = [...els].map((el) => el.style.cssText);
  const hadFade = [...els].map((el) => el.classList.contains("tq-fade-up"));
  els.forEach((el, i) => {
    el.classList.remove("tq-fade-up");
    el.style.opacity = "0";
    el.style.transform = `translateY(${rise}px)`;
    el.style.transition = `opacity ${dur}s ease ${(i % 4) * 0.08 / c.speed}s, transform ${dur}s ease ${(i % 4) * 0.08 / c.speed}s`;
  });
  const io = new IntersectionObserver((entries) => entries.forEach((e) => {
    if (e.isIntersecting) { e.target.style.opacity = "1"; e.target.style.transform = "translateY(0)"; io.unobserve(e.target); }
  }), { threshold: 0.1 });
  els.forEach((el) => io.observe(el));
  return () => { io.disconnect(); [...els].forEach((el, i) => { el.style.cssText = saved[i]; if (hadFade[i]) el.classList.add("tq-fade-up"); }); };
}

/* 4. 3D tilt on product image area */
function tilt3d(c) {
  const areas = document.querySelectorAll(`${scopeSel(c.target)} [data-testid^="product-card-"] > a`);
  if (!areas.length) return null;
  const max = 1.5 + c.intensity * 0.8;
  const handlers = [];
  areas.forEach((a) => {
    a.style.transformStyle = "preserve-3d";
    a.style.willChange = "transform";
    const move = (e) => {
      const r = a.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      a.style.transition = "transform .12s ease-out";
      a.style.transform = `perspective(700px) rotateY(${(x * max).toFixed(2)}deg) rotateX(${(-y * max).toFixed(2)}deg)`;
    };
    const leave = () => { a.style.transition = `transform ${0.5 / c.speed}s ease`; a.style.transform = "perspective(700px) rotateY(0) rotateX(0)"; };
    a.addEventListener("mousemove", move);
    a.addEventListener("mouseleave", leave);
    handlers.push([a, move, leave]);
  });
  return () => handlers.forEach(([a, move, leave]) => { a.removeEventListener("mousemove", move); a.removeEventListener("mouseleave", leave); a.style.transform = ""; a.style.transition = ""; });
}

/* 5. Magnetic CTA buttons */
function magneticButtons(c) {
  const sel = c.target === "hero" ? '[data-section="hero"] [data-testid="hero-cta"]'
    : c.target === "mid_banner" ? '[data-section="mid_banner"] [data-testid="banner-strip-cta"]'
    : '[data-section] .tq-btn-gold, [data-section] .tq-btn-dark';
  const btns = document.querySelectorAll(sel);
  if (!btns.length) return null;
  const strength = 2 + c.intensity * 1.5;
  const handlers = [];
  btns.forEach((btn) => {
    const move = (e) => {
      const r = btn.getBoundingClientRect();
      const x = e.clientX - (r.left + r.width / 2);
      const y = e.clientY - (r.top + r.height / 2);
      btn.style.transition = "transform .15s ease-out";
      btn.style.transform = `translate(${(x / r.width * strength).toFixed(1)}px, ${(y / r.height * strength).toFixed(1)}px)`;
    };
    const leave = () => { btn.style.transition = `transform ${0.45 / c.speed}s cubic-bezier(.2,.8,.3,1.2)`; btn.style.transform = "translate(0,0)"; };
    btn.addEventListener("mousemove", move);
    btn.addEventListener("mouseleave", leave);
    handlers.push([btn, move, leave]);
  });
  return () => handlers.forEach(([b, move, leave]) => { b.removeEventListener("mousemove", move); b.removeEventListener("mouseleave", leave); b.style.transform = ""; b.style.transition = ""; });
}

/* 6. Heading / text reveal */
function textReveal(c) {
  const sel = c.target === "hero" ? '[data-section="hero"]'
    : c.target === "sections" ? '[data-section]:not([data-section="hero"])'
    : "[data-section]";
  const els = document.querySelectorAll(`${sel} h1, ${sel} h2, ${sel} .tq-overline`);
  if (!els.length) return null;
  const dur = 0.8 / c.speed;
  const saved = [...els].map((el) => el.style.cssText);
  const hadFade = [...els].map((el) => el.classList.contains("tq-fade-up"));
  els.forEach((el) => {
    el.classList.remove("tq-fade-up");
    el.style.opacity = "0";
    el.style.transform = "translateY(16px)";
    el.style.transition = `opacity ${dur}s ease, transform ${dur}s ease`;
  });
  const io = new IntersectionObserver((entries) => entries.forEach((e) => {
    if (e.isIntersecting) { e.target.style.opacity = "1"; e.target.style.transform = "translateY(0)"; io.unobserve(e.target); }
  }), { threshold: 0.2 });
  els.forEach((el) => io.observe(el));
  return () => { io.disconnect(); [...els].forEach((el, i) => { el.style.cssText = saved[i]; if (hadFade[i]) el.classList.add("tq-fade-up"); }); };
}

/* 7. Floating / parallax hero product */
function heroParallax(c) {
  const hero = document.querySelector('[data-section="hero"]');
  if (!hero) return null;
  const drift = Math.min(90, 20 + c.intensity * 10);
  const scale = 1.04 + c.intensity * 0.01;
  const styleEl = document.createElement("style");
  styleEl.textContent = `@keyframes tq-hero-float{0%,100%{margin-top:0}50%{margin-top:${(-3 - c.intensity).toFixed(0)}px}}`;
  document.head.appendChild(styleEl);
  let styledImg = null;
  let savedCss = "";
  const apply = () => {
    const img = hero.querySelector("div.relative picture img") || hero.querySelector("picture img");
    if (!img || img === styledImg) return;
    if (styledImg) styledImg.style.cssText = savedCss;
    savedCss = img.style.cssText;
    styledImg = img;
    const y0 = Math.min(window.scrollY * 0.08 * (c.intensity / 4), drift);
    img.style.transition = "transform .1s linear";
    img.style.transformOrigin = "center top";
    img.style.transform = `translateY(${y0.toFixed(1)}px) scale(${scale})`;
    img.style.animation = `tq-hero-float ${(6 / c.speed).toFixed(1)}s ease-in-out infinite`;
  };
  apply();
  // Hero slider re-mounts the img on slide change — re-apply when the DOM mutates
  const mo = new MutationObserver(() => apply());
  mo.observe(hero, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
  let raf = 0;
  const onScroll = () => {
    if (raf || !styledImg) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      if (!styledImg) return;
      const y = Math.min(window.scrollY * 0.08 * (c.intensity / 4), drift);
      styledImg.style.transform = `translateY(${y.toFixed(1)}px) scale(${scale})`;
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  return () => {
    window.removeEventListener("scroll", onScroll);
    mo.disconnect();
    if (raf) cancelAnimationFrame(raf);
    styleEl.remove();
    if (styledImg) styledImg.style.cssText = savedCss;
  };
}

const EFFECTS = { stacked_swipe: stackedSwipe, scroll_reveal: scrollReveal, tilt_3d: tilt3d, magnetic_buttons: magneticButtons, text_reveal: textReveal, hero_parallax: heroParallax };

export function applyHomeAnimations(cfg) {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return () => {};
  const isMobile = window.matchMedia("(max-width: 768px)").matches;
  const finePointer = window.matchMedia("(pointer: fine)").matches;
  const deviceAllowed = (ec) => isMobile ? ec.mobile !== false : ec.desktop !== false;
  const cleanups = [];
  const t0 = setTimeout(() => {
    try {
      const hr = cfg.hover_reveal;
      if (deviceAllowed(hr)) { const cl = hoverReveal(hr); if (cl) cleanups.push(cl); }
    } catch { /* ignore */ }
    Object.entries(EFFECTS).forEach(([key, fn]) => {
      const ec = cfg[key];
      if (!ec?.enabled || !deviceAllowed(ec)) return;
      if ((key === "tilt_3d" || key === "magnetic_buttons") && !finePointer) return;
      try { const cl = fn(ec, cfg); if (cl) cleanups.push(cl); } catch { /* ignore */ }
    });
    if (isPreview()) cleanups.push(showPreviewBadge());
  }, 80);
  return () => { clearTimeout(t0); cleanups.forEach((fn) => { try { fn(); } catch { /* ignore */ } }); };
}
